import { useNotifications } from '@armhr/ui';
import { useCallback } from 'react';

import { useApi } from './useApi';

interface UseTerminationImportProps {
  onUploadSuccess: (rows: TerminationRow[]) => void;
  onUploadError: (error: string) => void;
  setCsvData: React.Dispatch<React.SetStateAction<TerminationRow[]>>;
  setIsUploading: (v: boolean) => void;
  setIsValidating: (v: boolean) => void;
  setIsValidated: (v: boolean) => void;
  setIsDownloadingTemplate: (v: boolean) => void;
  setIsSubmitting: (v: boolean) => void;
  setSubmitProgress: (v: number) => void;
  setSubmitResults: (results: BulkTerminationRowResult[]) => void;
}

export function useTerminationImport({
  onUploadSuccess,
  onUploadError,
  setCsvData,
  setIsUploading,
  setIsValidating,
  setIsValidated,
  setIsDownloadingTemplate,
  setIsSubmitting,
  setSubmitProgress,
  setSubmitResults,
}: UseTerminationImportProps) {
  const api = useApi();
  const { showNotification } = useNotifications();

  const handleDownloadExcelTemplate = useCallback(async () => {
    setIsDownloadingTemplate(true);
    try {
      const blob = await api.admin.downloadBulkTerminationExcelTemplate();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'bulk_termination_template.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      showNotification({
        message: 'Excel template downloaded successfully',
        severity: 'success',
        autoHideDuration: 3000,
      });
    } catch {
      showNotification({
        message: 'Failed to download Excel template. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsDownloadingTemplate(false);
    }
  }, [api, showNotification, setIsDownloadingTemplate]);

  const handleFileSelect = useCallback(
    async (file: File) => {
      const isExcel =
        file.name.endsWith('.xlsx') ||
        file.type ===
          'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

      if (!isExcel) {
        onUploadError('Please upload a valid Excel file (.xlsx)');
        return;
      }

      setIsUploading(true);

      try {
        const response = await api.admin.uploadBulkTerminationExcel(file);
        const parsed = response.results.map(
          (row: TerminationRow, idx: number) => ({
            ...row,
            rowIndex: idx + 2,
          })
        );
        onUploadSuccess(parsed);
        showNotification({
          message: `Successfully loaded ${parsed.length} rows from Excel file`,
          severity: 'success',
          autoHideDuration: 5000,
        });
      } catch (error) {
        onUploadError(
          error instanceof Error ? error.message : 'Error reading Excel file'
        );
      } finally {
        setIsUploading(false);
      }
    },
    [api, showNotification, onUploadSuccess, onUploadError, setIsUploading]
  );

  const handleValidate = useCallback(
    async (rows: TerminationRow[]) => {
      if (!rows.length) return;
      setIsValidating(true);
      try {
        const { results } = await api.admin.validateBulkTermination(rows);

        setCsvData((prev) =>
          prev.map((row, i) => {
            const result = results.find((r) => r.row === i);
            if (!result) return { ...row, errors: undefined };

            const msgs = result.validate_messages
              ?.map((m) => (m as Record<string, string>).errorString)
              .filter(Boolean);

            if (result.status === 'error') {
              result.message?.includes('500');
              return {
                ...row,
                errors: {
                  validation:
                    msgs?.join('; ') ||
                    (result.message?.includes('500')
                      ? 'Validation failed — verify the employee is still active and all required fields (status code, reason, term date, ACH) are filled in.'
                      : result.message || 'Validation failed'),
                },
              } as TerminationRow;
            }

            if (result.status === 'warning' || (msgs && msgs.length > 0)) {
              return {
                ...row,
                errors: {
                  warning: msgs?.join('; ') || result.message || 'Has warnings',
                },
              } as TerminationRow;
            }

            const { errors: _, ...clean } = row;
            return clean as TerminationRow;
          })
        );

        setIsValidated(true);
        showNotification({
          severity: 'success',
          message: `Validation complete for ${results.length} record(s).`,
        });
      } catch {
        showNotification({
          severity: 'error',
          message: 'Failed to validate termination records. Please try again.',
        });
      } finally {
        setIsValidating(false);
      }
    },
    [api, showNotification, setCsvData, setIsValidating, setIsValidated]
  );

  const handleExecute = useCallback(
    async (rows: TerminationRow[]) => {
      const eligible = rows.filter((r) => !r.errors);
      if (!eligible.length) return;

      setIsSubmitting(true);
      setSubmitProgress(0);

      try {
        const { results } = await api.admin.executeBulkTermination(eligible);
        setSubmitProgress(100);
        setSubmitResults(results);

        const succeeded = results.filter((r) => r.status === 'success').length;
        const failed = results.filter((r) => r.status === 'error').length;

        showNotification({
          severity: failed === 0 ? 'success' : 'warning',
          message:
            failed === 0
              ? `Successfully terminated ${succeeded} employee${
                  succeeded !== 1 ? 's' : ''
                }.`
              : `${succeeded} terminated, ${failed} failed. Review results below.`,
        });
      } catch {
        showNotification({
          severity: 'error',
          message: 'Failed to execute terminations. Please try again.',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      api,
      showNotification,
      setIsSubmitting,
      setSubmitProgress,
      setSubmitResults,
    ]
  );

  return {
    handleDownloadExcelTemplate,
    handleFileSelect,
    handleValidate,
    handleExecute,
  };
}
